import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import soundEffects from '../utils/soundEffects';
import { createRipple, createSparkle } from '../utils/vfx';
import './PatchNotes.css';

const PatchNotes = () => {
  useEffect(() => {
    soundEffects.playPageTransition();
  }, []);

  const patchNotes = [
    {
      version: '1.1.4',
      date: '2025-12-09',
      type: 'minor',
      changes: [
        {
          category: 'PDF Lesson Upload & AI Generation',
          items: [
            'Added comprehensive PDF upload functionality for creating lessons from PDF documents',
            'New AI-powered lesson generation using ChatGPT to create enhanced, information-rich lessons from PDF content',
            'Automatic quiz generation from PDF content with multiple question types (multiple choice, true/false, short answer)',
            'Rich text editor integration for editing AI-generated lesson content and quiz questions',
            'PDF upload available in both Admin Lessons View and Teacher Curriculum pages',
            'Multi-step workflow: Upload PDF → Preview Text → Generate Content → Edit Lesson → Edit Quiz',
            'Support for both regular lessons (admin) and teacher curriculum lessons (league-based)',
            'Enhanced lesson content with HTML formatting, headings, lists, and emphasis',
            'Editable quiz questions with rich text support for question text and explanations',
            'Automatic sanitization of quiz options to ensure proper formatting',
            'Improved lesson creation workflow with real-time content generation',
          ]
        },
        {
          category: 'Backend Enhancements',
          items: [
            'New PDF parsing service for extracting text from uploaded PDF files',
            'AI lesson service for generating enhanced educational content from PDF text',
            'New API endpoints for PDF upload, text extraction, and AI content generation',
            'Support for file uploads up to 10MB with proper validation',
            'Enhanced error handling and validation for PDF processing',
          ]
        },
        {
          category: 'User Experience Improvements',
          items: [
            'Intuitive drag-and-drop PDF upload interface',
            'Real-time preview of extracted PDF text before generation',
            'Step-by-step wizard for creating lessons from PDFs',
            'Visual feedback during AI content generation',
            'Seamless integration with existing lesson and quiz management systems',
          ]
        }
      ]
    },
    {
      version: '1.1.3',
      date: '2025-12-10',
      type: 'patch',
      changes: [
        {
          category: 'Portfolio Enhancements',
          items: [
            'Integrated watchlist stocks directly into portfolio view',
            'Added Featured Stocks section showing top 10 most traded stocks',
            'New collapsible dropdowns for watchlist, featured stocks, and portfolio holdings',
            'Enhanced stock search with improved dropdown interface',
            'Better organization of available stocks with watchlist priority display',
            'Auto-refresh functionality for watchlist and featured stocks every 5 seconds',
          ]
        },
        {
          category: 'Improvements',
          items: [
            'Optimized portfolio data loading and refresh mechanisms',
            'Better handling of stock price updates from multiple data sources',
            'Improved user experience with faster stock search and selection',
          ]
        }
      ]
    },
    {
      version: '1.2.0',
      date: '2025-12-5',
      type: 'minor',
      changes: [
        {
          category: 'Security & Permissions',
          items: [
            'Enhanced security measures with role-based access control',
            'Admin level restrictions for sensitive operations',
            'Improved data validation and error handling',
            'Protected admin transactions from deletion',
          ]
        },
        {
          category: 'Improvements',
          items: [
            'Optimized database queries for better performance',
            'Better error messages and user feedback',
            'Enhanced admin-to-admin communication system',
          ]
        }
      ]
    },
    {
      version: '1.1.2',
      date: '2024-12-01',
      type: 'patch',
      changes: [
        {
          category: 'Portfolio Update',
          items: [
            'Fixed the ability to add nonexistent stocks to the portfolio',
            'Helps keep your portfolio clean and relevant',
            'All business, no nonsense!',
          ]
        },
        {
          category: 'Watchlist',
          items: [
            'Stocks added to the portfolio are checked for validity',
            'Fixed crashes prompted by the addition of non-existent stocks to the watchlist',
          ]
        },
        {
          category: 'Learning Center',
          items: [
            'Quizzes now have updated material more relevant to your finances',
            'Added updated badges for achievements',
            'Added a leaderboard tracking portfolio and quiz progress',
          ]
        },
        {
          category: 'Profile Features',
          items: [
            'Added customizable user profiles',
            'Profile displays rank and achievements',
            'User statistics visible to other players',
            'Enhanced profile page with detailed progress tracking',
          ]
        }
      ]
    },
    {
      version: '1.1.0',
      date: '2024-11-08',
      type: 'minor',
      changes: [
        {
          category: 'Features',
          items: [
            'Added portfolio performance graphs with time-based visualization',
            'Enhanced leaderboard with filtering and sorting options',
            'Improved AI chat response accuracy and relevance',
            'Added detailed course progress tracking',
            'Portfolio gain/loss view with daily and all-time tracking',
            'Added Challenges system for interactive financial learning',
            'Implemented Forum feature for community discussions',
          ]
        },
        {
          category: 'Improvements',
          items: [
            'Optimized page load times and performance',
            'Improved mobile responsiveness across all pages',
            'Enhanced error handling and user feedback',
            'Updated stock data refresh rates for real-time accuracy',
            'Better portfolio calculation accuracy',
          ]
        },
        {
          category: 'Bug Fixes',
          items: [
            'Fixed portfolio calculation issues with stock prices',
            'Resolved authentication token expiration handling',
            'Fixed leaderboard ranking display and sorting',
            'Corrected quiz scoring system and XP rewards',
            'Fixed watchlist synchronization issues',
          ]
        }
      ]
    },
    {
      version: '1.0.0',
      date: '2024-10-15',
      type: 'major',
      changes: [
        {
          category: 'Features',
          items: [
            'Initial release of InvestNYou platform',
            'User authentication and registration system with guest mode',
            'Onboarding process for new users',
            'Educational course system with units, lessons, and quizzes',
            'Virtual portfolio simulation with real-time market data',
            'AI-powered financial Q&A chat feature',
            'XP and leveling system for user progress',
            'Badge and achievement system',
            'Leaderboard functionality',
            'Stock watchlist feature',
            'Daily financial facts with completion tracking',
            'Stock search and trading functionality',
            'Portfolio transaction history',
            'User profile system',
          ]
        },
        {
          category: 'UI/UX',
          items: [
            'Modern and responsive design',
            'Intuitive navigation system',
            'Mobile-friendly interface',
            'Smooth animations and transitions',
            'Sound effects and visual feedback',
            'Accessible design with keyboard navigation',
          ]
        },
        {
          category: 'Security',
          items: [
            'Secure JWT-based authentication',
            'Password encryption and secure storage',
            'IP tracking for security and DDoS protection',
            'Rate limiting and abuse prevention',
          ]
        }
      ]
    }
  ];

  const getVersionTypeClass = (type) => {
    switch (type) {
      case 'major':
        return 'version-major';
      case 'minor':
        return 'version-minor';
      case 'patch':
        return 'version-patch';
      default:
        return '';
    }
  };

  const getVersionTypeLabel = (type) => {
    switch (type) {
      case 'major':
        return 'Major Update';
      case 'minor':
        return 'Minor Update';
      case 'patch':
        return 'Patch';
      default:
        return 'Update';
    }
  };

  return (
    <div className="patchnotes-container">
      {/* Back Button */}
      <Link 
        to="/" 
        className="patchnotes-back-button"
        onClick={(e) => {
          soundEffects.playClick();
          createRipple(e, '#ffffff');
        }}
        onMouseEnter={() => soundEffects.playHover()}
      >
        ← Back
      </Link>
      <div className="patchnotes-content">
        <div className="patchnotes-header">
          <h1>Patch Notes</h1>
          <p className="subtitle">Stay updated with the latest changes and improvements to InvestNYou</p>
        </div>

        {patchNotes.map((patch, index) => (
          <section key={index} className="patch-version">
            <div className="version-header">
              <div className="version-info">
                <h2 className={getVersionTypeClass(patch.type)}>
                  Version {patch.version}
                </h2>
                <span className="version-type">{getVersionTypeLabel(patch.type)}</span>
              </div>
              <p className="version-date">{new Date(patch.date).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</p>
            </div>

            {patch.changes.map((changeGroup, groupIndex) => (
              <div key={groupIndex} className="change-group">
                <h3 className="change-category">{changeGroup.category}</h3>
                <ul className="change-list">
                  {changeGroup.items.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        ))}

        <div className="patchnotes-footer">
          <p className="footer-note">
            Want to suggest a feature or report a bug? Contact us at{' '}
            <a href="mailto:investnyouteam@gmail.com">investnyouteam@gmail.com</a>
          </p>
        </div>

        <div className="legal-links">
          <Link 
            to="/terms" 
            className="legal-link"
            onClick={(e) => {
              soundEffects.playClick();
              createRipple(e);
            }}
            onMouseEnter={() => soundEffects.playHover()}
          >
            Terms of Service
          </Link>
          <Link 
            to="/privacy" 
            className="legal-link"
            onClick={(e) => {
              soundEffects.playClick();
              createRipple(e);
            }}
            onMouseEnter={() => soundEffects.playHover()}
          >
            Privacy Policy
          </Link>
          <Link 
            to="/" 
            className="legal-link"
            onClick={(e) => {
              soundEffects.playClick();
              createRipple(e);
            }}
            onMouseEnter={() => soundEffects.playHover()}
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PatchNotes;







